import {Vector2, Vector4, Scene, Group, WebGLRenderTarget, Mesh, PlaneGeometry, ShaderMaterial, Color} from 'three'

import common from './common'
import Shield from './shield'
import mouseMng from './mouse'
import shaders from './glsl/shaders'
import Lines from './lines'
import Timeline from './utils/timeline'
import GaussianBlur from './gaussian-blur'
import {commonUniforms} from './utils/common-uniforms'

import Halo from './halo'
import heroParams from './params/hero'

export default class MainScene {
  scene_whole: Scene = new Scene()
  scene_main: Scene = new Scene()
  scene_mask: Scene = new Scene()
  scene_shield: Scene = new Scene()
  scene_gitlines: Scene = new Scene()
  group_main: Group = new Group()
  mouseIntensity: Vector4 = new Vector4(0, 0, 0, 0)
  maskRatio: number = 0.5
  circleRatio: number = 0.3
  haloRatio: number = 0.2
  progress: Vector4 = new Vector4(0, 0, 0, 0)
  fbo_gitlines: WebGLRenderTarget
  fbo_shield: WebGLRenderTarget
  circleResolution: Vector2
  fbo_mask: WebGLRenderTarget
  fbo_halo: WebGLRenderTarget
  fbo_shieldBack: WebGLRenderTarget
  fbo_circle: WebGLRenderTarget
  gaussianBlur: GaussianBlur
  plane_circle?: Mesh
  output?: Mesh
  plane_maskImage?: Mesh
  shield?: Shield
  shieldBackGroup?: Group
  halo?: Halo

  constructor() {
    this.scene_main.add(this.group_main)
    this.fbo_gitlines = new WebGLRenderTarget(common.fbo_screenSize.x, common.fbo_screenSize.y)
    this.fbo_shield = new WebGLRenderTarget(common.fbo_screenSize.x, common.fbo_screenSize.y)

    this.circleResolution = new Vector2(
      Math.round(common.fbo_screenSize.x * this.circleRatio),
      Math.round(common.fbo_screenSize.y * this.circleRatio),
    )

    this.fbo_mask = new WebGLRenderTarget(
      common.fbo_screenSize.x * this.maskRatio,
      common.fbo_screenSize.y * this.maskRatio,
    )
    this.fbo_halo = new WebGLRenderTarget(
      common.fbo_screenSize.x * this.haloRatio,
      common.fbo_screenSize.y * this.haloRatio,
    )
    this.fbo_shieldBack = new WebGLRenderTarget(common.fbo_screenSize.x, common.fbo_screenSize.y)
    this.fbo_circle = new WebGLRenderTarget(this.circleResolution.x, this.circleResolution.y)
    this.gaussianBlur = new GaussianBlur(this.fbo_mask, 30)
  }

  init() {
    const progress = new Vector4(0, 0, 0, 0)

    const tl = new Timeline({
      delay: common.delay.middle,
    })
    tl.to([progress], 2000, {x: 1.0, easing: 'easeOutCubic'}, 0.0)
    tl.start()

    this.plane_circle = new Mesh(
      new PlaneGeometry(2, 2),
      new ShaderMaterial({
        vertexShader: shaders.screenVert,
        fragmentShader: shaders.circleFrag,
        uniforms: {
          uResolution: {
            value: this.circleResolution,
          },
          uTime: commonUniforms.uTime,
          uBeatSpeed: commonUniforms.uBeatSpeed,
        },
      }),
    )

    this.output = new Mesh(
      new PlaneGeometry(2, 2),
      new ShaderMaterial({
        vertexShader: shaders.screenVert,
        fragmentShader: shaders.utils + shaders.outputFrag,
        uniforms: {
          uShield: {
            value: this.fbo_shield.texture,
          },
          uGitlines: {
            value: this.fbo_gitlines.texture,
          },
          uMask: {
            value: this.fbo_mask.texture,
          },
          uBgColor: commonUniforms.uBgColor,
          uGlowColor: {
            value: new Color(0x6bd6d0),
          },
          uBlur: {
            value: this.gaussianBlur.blurFbos[1] ? this.gaussianBlur.blurFbos[1].texture : null,
          },
          uHalo: {
            value: this.fbo_halo.texture,
          },
          uShieldBack: {
            value: this.fbo_shieldBack.texture,
          },
          uTime: commonUniforms.uTime,
          uBeatSpeed: commonUniforms.uBeatSpeed,
          uProgress: {
            value: this.progress,
          },
          uColor1: {
            value: new Color(0x2084da),
          },
          uColor2: {
            value: new Color(0x52b9d2),
          },
          uColor3: {
            value: new Color(0x94dd3e),
          },
          uColor4: {
            value: new Color(0x2637d4),
          },
          uResolution: {
            value: common.fbo_screenSize,
          },
        },
        transparent: true,
      }),
    )

    this.scene_whole.add(this.output)

    this.plane_maskImage = new Mesh(
      new PlaneGeometry(2, 2),
      new ShaderMaterial({
        vertexShader: shaders.screenVert,
        fragmentShader: shaders.maskImageFrag,
        uniforms: {
          uShield: {
            value: this.fbo_shield.texture,
          },
          uGitlines: {
            value: this.fbo_gitlines.texture,
          },
          uCircle: {
            value: this.fbo_circle.texture,
          },
          uResolution: {
            value: common.fbo_screenSize,
          },
          uGlowColor: {
            value: new Color(0x6bd6d0),
          },
        },
        transparent: true,
      }),
    )

    this.scene_mask.add(this.plane_maskImage)

    this.initShieldScene()
    this.initGirlinesScene()

    this.halo = new Halo()

    this.createOpening()
  }

  createOpening() {
    if (this.shield && this.halo) {
      const tl = new Timeline({
        delay: 0,
      })

      const scale = 1.25

      tl.to([this.shield.lightPos], 1000 * scale, {z: 0.1, easing: 'easeOutCubic'}, 0.0)
      tl.to([this.shield.progress], 2000 * scale, {y: 1, easing: 'easeOutCubic'}, 0.0)
      tl.to([this.shield.progress], 3000 * scale, {x: 1, easing: 'easeOutCubic'}, 600 * scale)
      tl.to([this.shield.progress], 3000 * scale, {z: 1, easing: 'easeOutCubic'}, 800 * scale)
      tl.to([this.halo.progress], 4000 * scale, {x: 1, easing: 'easeOutCubic'}, 0.0)
      tl.to([this.progress], 1000 * scale, {x: 1, easing: 'easeOutCubic'}, 500 * scale)
      tl.to([this.mouseIntensity], 1000 * scale, {x: 1, easing: 'easeOutCubic'}, 2000 * scale)

      tl.start()
    }
  }

  initShieldScene() {
    const group = new Group()
    this.scene_shield.add(group)

    group.position.copy(heroParams.group.position)
    group.rotation.copy(heroParams.group.rotation)

    const tl = new Timeline({
      delay: common.delay.main,
    })

    this.shield = new Shield()
    this.shield.init()

    this.shieldBackGroup = this.shield.shieldBackGroup

    this.shield.group.position.set(0, -0.05, 0.0)
    this.shield.group.scale.set(6, 6, 6)

    this.shieldBackGroup.position.copy(this.shield.group.position)
    this.shieldBackGroup.scale.set(this.shield.group.scale.x, this.shield.group.scale.y, this.shield.group.scale.z)

    group.add(this.shield.group)

    tl.start()
  }

  initGirlinesScene() {
    const lines = new Lines(heroParams.group.lines)
    this.scene_gitlines.add(lines.group)
    lines.init()
  }

  resize() {
    this.circleResolution.set(
      Math.round(common.fbo_screenSize.x * this.circleRatio),
      Math.round(common.fbo_screenSize.y * this.circleRatio),
    )

    this.fbo_gitlines.setSize(common.fbo_screenSize.x, common.fbo_screenSize.y)
    this.fbo_shield.setSize(common.fbo_screenSize.x, common.fbo_screenSize.y)

    this.fbo_mask.setSize(common.fbo_screenSize.x * this.maskRatio, common.fbo_screenSize.y * this.maskRatio)
    this.fbo_circle.setSize(this.circleResolution.x, this.circleResolution.y)

    this.fbo_halo.setSize(common.fbo_screenSize.x * this.haloRatio, common.fbo_screenSize.y * this.haloRatio)

    this.fbo_shieldBack.setSize(common.fbo_screenSize.x, common.fbo_screenSize.y)

    this.gaussianBlur.resize()
  }

  update() {
    if (this.shield) {
      this.shield.update()
    }

    const x = mouseMng.pos.current2.x * heroParams.mouseParallax[0]
    const y = mouseMng.pos.current2.y * heroParams.mouseParallax[0]

    common.camera.position.x = x * this.mouseIntensity.x
    common.camera.position.y = y * this.mouseIntensity.x
    common.camera.position.z = common.cameraZ

    const l = common.camera.position.length()
    common.camera.position.multiplyScalar(1 / (l / common.cameraZ))
    common.camera.lookAt(common.center)

    common.camera2.position.x = mouseMng.pos.current2.x * heroParams.mouseParallax[1]
    common.camera2.position.y = mouseMng.pos.current2.y * heroParams.mouseParallax[1]
    common.camera2.lookAt(common.center)
  }

  renderMainScene() {
    if (this.plane_circle) {
      common.renderer?.setClearColor(0x000000, 1.0)
      common.renderer?.setRenderTarget(this.fbo_circle)
      common.renderer?.render(this.plane_circle, common.camera)
    }

    common.renderer?.setClearColor(0xffffff, 0.0)
    common.renderer?.setRenderTarget(this.fbo_gitlines)
    common.renderer?.render(this.scene_gitlines, common.camera)

    common.renderer?.setClearColor(0xffffff, 0.0)
    common.renderer?.setRenderTarget(this.fbo_shield)
    common.renderer?.render(this.scene_shield, common.camera)

    if (this.shieldBackGroup) {
      common.renderer?.setRenderTarget(this.fbo_shieldBack)
      common.renderer?.render(this.shieldBackGroup, common.camera)
    }

    common.renderer?.setClearColor(0x000000, 1.0)
    common.renderer?.setRenderTarget(this.fbo_mask)
    common.renderer?.render(this.scene_mask, common.camera)

    if (this.halo) {
      this.halo.update()
      common.renderer?.setClearColor(0xffffff, 0.0)
      common.renderer?.setRenderTarget(this.fbo_halo)
      common.renderer?.render(this.halo.scene, common.camera)
    }

    this.gaussianBlur.update()
  }

  renderWholeScene() {
    common.renderer?.setClearColor(0x000000)
    common.renderer?.setRenderTarget(null)
    common.renderer?.render(this.scene_whole, common.camera)
  }
}
